import { createContext, useContext, useId, useMemo } from 'react'
import { ResponsiveContainer, Tooltip } from 'recharts'
import { cn } from '@/lib/cn'

/**
 * Shadcn-style chart primitives over Recharts.
 *
 * A chart declares its series once as a {@link ChartConfig}; the container turns
 * that into scoped CSS custom properties (`--color-<key>`) so marks reference
 * `var(--color-x)` instead of hard-coded hex, and light/dark swap in one place.
 */

/** Per-series presentation. `color` is a single value; `theme` splits light/dark. */
export type ChartSeriesConfig = {
  label?: React.ReactNode
  icon?: React.ComponentType<{ className?: string }>
} & ({ color?: string; theme?: never } | { color?: never; theme: Record<'light' | 'dark', string> })

export type ChartConfig = Record<string, ChartSeriesConfig>

interface ChartContextValue {
  config: ChartConfig
}

const ChartContext = createContext<ChartContextValue | null>(null)

function useChartContext(): ChartContextValue {
  const ctx = useContext(ChartContext)
  if (ctx == null) {
    throw new Error('Chart components must be used within a <ChartContainer />')
  }
  return ctx
}

/** Emits `--color-<key>` for every configured series, once per mode. */
function ChartStyle({ id, config }: { id: string; config: ChartConfig }): React.ReactElement | null {
  const entries = Object.entries(config).filter(([, item]) => item.color || item.theme)
  if (entries.length === 0) return null

  const rules = (mode: 'light' | 'dark'): string =>
    entries
      .map(([key, item]) => {
        const color = item.theme?.[mode] ?? item.color
        return color ? `  --color-${key}: ${color};` : null
      })
      .filter(Boolean)
      .join('\n')

  // Dark mode is class-based (`html.dark`) in this app, so scope the dark block
  // to that class rather than a prefers-color-scheme media query.
  return (
    <style
      dangerouslySetInnerHTML={{
        __html: `[data-chart="${id}"] {\n${rules('light')}\n}\nhtml.dark [data-chart="${id}"] {\n${rules('dark')}\n}`,
      }}
    />
  )
}

export interface ChartContainerProps extends React.ComponentProps<'div'> {
  config: ChartConfig
  /** A single Recharts chart element. */
  children: React.ComponentProps<typeof ResponsiveContainer>['children']
}

/**
 * Wraps a Recharts chart with the config-driven color variables and the
 * recessive grid/axis/legend styling the charts share.
 */
export function ChartContainer({
  id,
  className,
  children,
  config,
  ...rest
}: ChartContainerProps): React.ReactElement {
  const uniqueId = useId()
  const chartId = `chart-${(id ?? uniqueId).replace(/:/g, '')}`
  const ctx = useMemo(() => ({ config }), [config])

  return (
    <ChartContext.Provider value={ctx}>
      <div
        data-chart={chartId}
        className={cn(
          "flex aspect-video justify-center text-xs",
          // Recessive chrome: hairline grid, muted ticks, no default axis lines.
          '[&_.recharts-cartesian-axis-tick_text]:fill-gray-500 dark:[&_.recharts-cartesian-axis-tick_text]:fill-gray-400',
          '[&_.recharts-cartesian-grid_line]:stroke-gray-200 dark:[&_.recharts-cartesian-grid_line]:stroke-gray-700/70',
          '[&_.recharts-cartesian-axis_line]:stroke-transparent [&_.recharts-cartesian-axis-tick_line]:stroke-transparent',
          '[&_.recharts-sector]:outline-none [&_.recharts-surface]:outline-none [&_.recharts-layer]:outline-none',
          className,
        )}
        {...rest}
      >
        <ChartStyle id={chartId} config={config} />
        <ResponsiveContainer>{children}</ResponsiveContainer>
      </div>
    </ChartContext.Provider>
  )
}

ChartContainer.displayName = 'ChartContainer'

/** Recharts `<Tooltip>` preconfigured with the shared cursor treatment. */
export const ChartTooltip = Tooltip

interface TooltipPayloadEntry {
  name?: string | number
  value?: number | string
  dataKey?: string | number
  color?: string
  payload?: Record<string, unknown>
}

export interface ChartTooltipContentProps {
  active?: boolean
  payload?: TooltipPayloadEntry[]
  label?: string | number
  /** Formats the numeric value (e.g. currency). */
  formatter?: (value: number) => string
  /** Overrides the heading; defaults to the axis label. */
  labelFormatter?: (label: string | number) => string
  /** Hides the heading row — useful for single-slice pie tooltips. */
  hideLabel?: boolean
  className?: string
}

/**
 * Card-style tooltip: a color swatch per series, name on the left, value
 * right-aligned in tabular figures so stacked rows line up.
 */
export function ChartTooltipContent({
  active,
  payload,
  label,
  formatter,
  labelFormatter,
  hideLabel = false,
  className,
}: ChartTooltipContentProps): React.ReactElement | null {
  const { config } = useChartContext()

  if (!active || !payload?.length) return null

  const heading = labelFormatter ? labelFormatter(label ?? '') : label

  return (
    <div
      className={cn(
        'min-w-[9rem] rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs shadow-lg',
        'dark:border-gray-700 dark:bg-gray-900',
        className,
      )}
    >
      {!hideLabel && heading != null && heading !== '' && (
        <p className="mb-1.5 font-medium text-gray-900 dark:text-gray-100">{heading}</p>
      )}
      <div className="space-y-1">
        {payload.map((entry, index) => {
          const key = String(entry.dataKey ?? entry.name ?? index)
          const seriesLabel = config[key]?.label ?? entry.name ?? key
          const numeric = typeof entry.value === 'number' ? entry.value : Number(entry.value) || 0
          const swatch = entry.color ?? `var(--color-${key})`
          return (
            <div key={key} className="flex items-center gap-2">
              <span
                className="h-2.5 w-2.5 shrink-0 rounded-[2px]"
                style={{ backgroundColor: swatch }}
                aria-hidden
              />
              <span className="flex-1 truncate text-gray-600 dark:text-gray-400">{seriesLabel}</span>
              <span className="font-semibold tabular-nums text-gray-900 dark:text-gray-100">
                {formatter ? formatter(numeric) : numeric.toLocaleString()}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

ChartTooltipContent.displayName = 'ChartTooltipContent'
